/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useCallback } from "react";
import { Loader2, Upload, Search, X, Trash2, FileText, Image, File, CheckCircle } from "lucide-react";

const DOC_PAGE_SIZE = 10;

const resolveFileLink = (file) => {
    if (file?.objectKey) return "__objectKey__";
    return file?.signedUrl || file?.downloadUrl || file?.previewUrl || null;
};

// File type icon helper
const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
        return <Image className="w-5 h-5 text-emerald-400" />;
    }
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
        return <FileText className="w-5 h-5 text-sky-400" />;
    }
    return <File className="w-5 h-5 text-slate-400" />;
};

const DocumentsWidget = ({
    caseId,
    caseFiles = {},
    isUploading,
    uploadMessage,
    uploadProgress,
    isCaseHydrating,
    onDocumentUpload,
    handlePreview,
    onDeleteFile,
    isDeletingFile,
    fileDeleteTarget
}) => {
    const [documentSearchTerm, setDocumentSearchTerm] = useState("");
    const [docPage, setDocPage] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);
    const dropZoneRef = useRef(null);
    const fileInputRef = useRef(null);

    const currentFiles = caseFiles[caseId] || [];

    const filteredFiles = currentFiles.filter((file) => {
        if (!documentSearchTerm.trim()) return true;
        const term = documentSearchTerm.toLowerCase();
        return (
            (file.name || "").toLowerCase().includes(term) ||
            (file.source || "").toLowerCase().includes(term)
        );
    });

    const displayFiles = filteredFiles.slice(0, docPage * DOC_PAGE_SIZE);
    const hasMore = filteredFiles.length > docPage * DOC_PAGE_SIZE;

    // Drag and drop handlers
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter(prev => prev + 1);
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter(prev => {
            const newCount = prev - 1;
            if (newCount === 0) {
                setIsDragging(false);
            }
            return newCount;
        });
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setDragCounter(0);

        if (isUploading) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            // Create a synthetic event object that matches what the input onChange expects
            const syntheticEvent = {
                target: {
                    files: files
                }
            };
            onDocumentUpload(syntheticEvent);
        }
    }, [isUploading, onDocumentUpload]);

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="border dark:border-slate-800 border-slate-200 rounded-xl p-5 dark:bg-slate-900/70 bg-white shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <p className="text-sm font-semibold dark:text-white text-slate-900">Upload documents</p>
                    <p className="text-xs dark:text-slate-500 text-slate-500">Attach credit, income, or collateral docs</p>
                </div>
                <button
                    onClick={handleButtonClick}
                    disabled={isUploading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 dark:text-slate-200 text-slate-700 cursor-pointer dark:hover:bg-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin text-sky-400" /> : <Upload className="w-4 h-4" />}
                    <span className="text-sm font-semibold">{isUploading ? "Uploading..." : "Add Files"}</span>
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xls,.xlsx"
                    onChange={onDocumentUpload}
                    className="hidden"
                    disabled={isUploading}
                />
            </div>

            {/* Drag & Drop Zone */}
            <div
                ref={dropZoneRef}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`relative mb-4 border-2 border-dashed rounded-xl transition-all duration-200 ${
                    isDragging
                        ? 'border-sky-500 bg-sky-500/10 dark:bg-sky-500/5'
                        : 'border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600'
                }`}
            >
                <div className={`flex flex-col items-center justify-center py-8 px-4 text-center transition-all ${
                    isDragging ? 'scale-105' : ''
                }`}>
                    <div className={`p-4 rounded-full mb-3 transition-all ${
                        isDragging
                            ? 'bg-sky-500/20 dark:bg-sky-500/10'
                            : 'bg-slate-100 dark:bg-slate-800'
                    }`}>
                        <Upload className={`w-8 h-8 transition-colors ${
                            isDragging
                                ? 'text-sky-500'
                                : 'text-slate-400 dark:text-slate-500'
                        }`} />
                    </div>
                    <p className={`text-sm font-medium mb-1 transition-colors ${
                        isDragging
                            ? 'text-sky-500 dark:text-sky-400'
                            : 'dark:text-slate-300 text-slate-600'
                    }`}>
                        {isDragging ? 'Drop files here' : 'Drag and drop files here'}
                    </p>
                    <p className="text-xs text-slate-500">
                        or click "Add Files" to browse
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                        PDF, DOC, DOCX, TXT, JPG, PNG, XLS, XLSX
                    </p>
                </div>

                {/* Drag overlay */}
                {isDragging && (
                    <div className="absolute inset-0 bg-sky-500/5 dark:bg-sky-500/10 rounded-xl pointer-events-none flex items-center justify-center">
                        <div className="bg-sky-500 text-white px-4 py-2 rounded-lg shadow-lg font-medium text-sm">
                            Release to upload
                        </div>
                    </div>
                )}
            </div>

            {/* Upload Progress */}
            {uploadMessage && (
                <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <p className="dark:text-emerald-400 text-emerald-600 text-sm">{uploadMessage}</p>
                </div>
            )}
            {isUploading && (
                <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <span>Uploading files...</span>
                        <span>{uploadProgress ? `${uploadProgress}%` : ""}</span>
                    </div>
                    <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-sky-500 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress || 0}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Document Library */}
            <div className="dark:bg-slate-950 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-lg p-4 max-h-72 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Document Library</p>
                    <div className="flex items-center gap-3">
                        {isCaseHydrating && (
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                                <Loader2 className="w-3 h-3 animate-spin text-sky-400" />
                                <span>Refreshing...</span>
                            </div>
                        )}
                        <span className="text-xs text-slate-400">
                            {currentFiles.length} file{currentFiles.length === 1 ? "" : "s"}
                        </span>
                    </div>
                </div>

                {/* Search */}
                {currentFiles.length > 0 && (
                    <div className="mb-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                                type="text"
                                placeholder="Search documents..."
                                value={documentSearchTerm}
                                onChange={(e) => setDocumentSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 rounded-lg text-sm dark:text-slate-200 text-slate-900 dark:placeholder-slate-500 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                            />
                            {documentSearchTerm && (
                                <button
                                    onClick={() => setDocumentSearchTerm("")}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {currentFiles.length > 0 ? (
                    <>
                        <ul className="space-y-2">
                            {displayFiles.map((file, index) => (
                                <li
                                    key={`${file.name}-${file.uploadedAt || index}`}
                                    className="flex items-center gap-3 justify-between text-sm dark:text-slate-200 text-slate-700 dark:bg-slate-900 bg-white rounded-lg px-3 py-2.5 border dark:border-slate-800 border-slate-200 hover:border-sky-500/50 dark:hover:border-sky-500/50 transition-colors group"
                                >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        {getFileIcon(file.name)}
                                        <div className="flex-1 min-w-0">
                                            <p className="truncate font-semibold">{file.name}</p>
                                            <p className="text-xs text-slate-500">
                                                {file.source || "Uploaded"}
                                                {file.uploadedAt ? ` • ${new Date(file.uploadedAt).toLocaleString()}` : ""}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                                        {(file.objectKey || resolveFileLink(file)) ? (
                                            <button
                                                onClick={() => {
                                                    const raw = resolveFileLink(file);
                                                    const link = raw === "__objectKey__" ? null : raw;
                                                    handlePreview({
                                                        ...file,
                                                        previewUrl: link,
                                                        url: link,
                                                        type: file.type || file.contentType || "application/pdf"
                                                    });
                                                }}
                                                className="px-3 py-1 rounded-lg border dark:border-sky-500/40 border-sky-400/40 dark:text-sky-300 text-sky-700 hover:text-sky-800 dark:hover:text-sky-200 dark:hover:border-sky-400 hover:bg-sky-50 dark:hover:bg-transparent transition-colors text-xs font-semibold"
                                                disabled={isCaseHydrating}
                                            >
                                                {isCaseHydrating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
                                            </button>
                                        ) : (
                                            <span className="text-xs text-slate-500">No preview</span>
                                        )}
                                        <button
                                            type="button"
                                            className="p-2 rounded-lg dark:text-slate-400 text-slate-500 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-500/10 transition-colors"
                                            onClick={() => onDeleteFile && onDeleteFile(file)}
                                            disabled={isDeletingFile && fileDeleteTarget === file.name}
                                        >
                                            {isDeletingFile && fileDeleteTarget === file.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {hasMore && (
                            <div className="mt-3 flex justify-center">
                                <button
                                    onClick={() => setDocPage((prev) => prev + 1)}
                                    className="px-3 py-1 rounded-lg border dark:border-slate-700 border-slate-300 dark:text-slate-200 text-slate-600 text-xs hover:border-sky-500 dark:hover:border-sky-500 transition-colors"
                                >
                                    Show more files
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-6">
                        <File className="w-10 h-10 text-slate-400 dark:text-slate-600 mx-auto mb-2" />
                        <p className="text-slate-500 text-sm">No documents uploaded yet.</p>
                        <p className="text-slate-400 text-xs mt-1">Drag files here or click Add Files</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentsWidget;
